
import React from 'react';
import { Button } from '@/components/ui/button';
import { DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CivicEducationItem } from '@/types/civic-education';
import BasicInfoFields from './form/BasicInfoFields';
import PodcastFields from './form/PodcastFields';
import VideoFields from './form/VideoFields';
import SummaryFields from './form/SummaryFields';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useState } from 'react';

interface CivicEducationFormProps {
  item: CivicEducationItem;
  setItem: (item: CivicEducationItem) => void;
  onCancel: () => void;
  onSave: () => Promise<boolean>;
  isEditing: boolean;
  contentType: string;
}

const CivicEducationForm = ({ 
  item, 
  setItem, 
  onCancel, 
  onSave,
  isEditing
}: CivicEducationFormProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showValidationAlert, setShowValidationAlert] = useState(false);

  const validateForm = () => {
    if (!item.title.trim()) {
      setValidationError('Title is required');
      return false;
    }
    
    if (!item.description.trim()) {
      setValidationError('Description is required');
      return false;
    }
    
    // Type-specific validation
    if (item.type === 'podcast' && !item.episode_id.trim()) {
      setValidationError('Episode ID is required for podcasts');
      return false;
    }
    
    if (item.type === 'video' && !item.video_id.trim()) {
      setValidationError('Video ID is required for videos');
      return false;
    }
    
    if (item.type === 'summary') {
      const summary = item as any;
      if (!summary.content.trim()) {
        setValidationError('Content is required for summaries');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setShowValidationAlert(true);
      return;
    }
    
    setIsSaving(true);
    try {
      const success = await onSave();
      if (success) {
        onCancel();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: keyof CivicEducationItem, value: any) => {
    setItem({ ...item, [field]: value });
  };

  return (
    <>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Content' : 'Add New Content'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <BasicInfoFields item={item} onFieldChange={handleFieldChange} />

          <Tabs defaultValue="content-details" className="w-full">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="content-details">Content Details</TabsTrigger>
            </TabsList>

            <TabsContent value="content-details" className="space-y-4 mt-4">
              {item.type === 'podcast' && (
                <PodcastFields item={item} onFieldChange={handleFieldChange} />
              )}

              {item.type === 'video' && (
                <VideoFields item={item} onFieldChange={handleFieldChange} />
              )}

              {item.type === 'summary' && (
                <SummaryFields item={item} onFieldChange={handleFieldChange} />
              )}
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              type="button" 
              onClick={onCancel} 
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
      
      <AlertDialog open={showValidationAlert} onOpenChange={setShowValidationAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Validation Error</AlertDialogTitle>
            <AlertDialogDescription>
              {validationError}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CivicEducationForm;
